import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { isValidEmail } from "@/features/workspaces/workspaces.utils";
import { useState } from "react";
import type { FormEvent } from "react";
import { GrantCreateWorkspaceError, type GrantCreateWorkspaceErrorKey } from "./api/invites-api";
import { useGrantCreateWorkspacePermissionMutation } from "./api/invites-hooks";

type FormErrorKey = GrantCreateWorkspaceErrorKey | "invalid_email";

function errorMessage(key: FormErrorKey): string {
  switch (key) {
    case "invalid_email":
      return "Email is not valid.";
    case "not_authorized":
      return "Only the Kairo team can grant workspace creation.";
    case "profile_not_found":
      return "No Kairo account uses this email.";
    case "ambiguous_email":
      return "Several accounts use this email. Contact engineering.";
    default:
      return "Failed to grant workspace creation. Please try again.";
  }
}

// CR-208: the create_workspace permission is consumed by the first workspace creation;
// this dialog lets a superuser give one account exactly one more creation.
export function SuperuserGrantWorkspaceCreation() {
  const grantMutation = useGrantCreateWorkspacePermissionMutation();

  const [isOpen, setIsOpen] = useState(false);
  const [email, setEmail] = useState("");
  // A key, not the message: copy is a catalog lookup, so the key is what is tested.
  const [formError, setFormError] = useState<FormErrorKey | null>(null);
  const [grantedEmail, setGrantedEmail] = useState<string | null>(null);

  function handleOpenChange(open: boolean) {
    setIsOpen(open);
    if (open) {
      setEmail("");
      setFormError(null);
    }
  }

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const trimmedEmail = email.trim();

    if (!isValidEmail(trimmedEmail)) {
      setFormError("invalid_email");
      return;
    }

    setFormError(null);
    try {
      await grantMutation.mutateAsync(trimmedEmail);
      setGrantedEmail(trimmedEmail);
      setIsOpen(false);
    } catch (error) {
      setFormError(error instanceof GrantCreateWorkspaceError ? error.key : "unknown");
    }
  }

  return (
    <div className="flex items-center gap-3">
      {!!grantedEmail && (
        <span
          data-testid="superuser-grant-workspace-creation-success"
          className="text-sm text-muted-foreground"
        >
          Workspace creation granted to {grantedEmail}.
        </span>
      )}
      <Dialog open={isOpen} onOpenChange={handleOpenChange}>
        <DialogTrigger asChild>
          <Button
            type="button"
            variant="outline"
            data-testid="superuser-grant-workspace-creation-trigger"
          >
            Grant workspace creation
          </Button>
        </DialogTrigger>
        <DialogContent className="sm:max-w-lg">
          <form
            noValidate
            data-testid="superuser-grant-workspace-creation-form"
            className="space-y-6"
            onSubmit={(event) => void handleSubmit(event)}
          >
            <DialogHeader>
              <DialogTitle>Grant workspace creation</DialogTitle>
              <DialogDescription>
                The account can create one more workspace. The right is used up by that creation.
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-2">
              <Label htmlFor="grantWorkspaceCreationEmail">Email</Label>
              <Input
                id="grantWorkspaceCreationEmail"
                type="email"
                autoComplete="off"
                value={email}
                onChange={(event) => setEmail(event.target.value)}
                disabled={grantMutation.isPending}
              />
            </div>

            {!!formError && (
              <Alert variant="destructive" data-testid="superuser-grant-workspace-creation-error">
                <AlertDescription>{errorMessage(formError)}</AlertDescription>
              </Alert>
            )}

            <DialogFooter>
              <Button
                type="submit"
                data-testid="superuser-grant-workspace-creation-submit"
                disabled={grantMutation.isPending}
              >
                Grant
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
